/**
 * BulletinCards — the morning bulletin's agent-card grid + action items
 * (v0.5.6 AE-093). Replaces the 6am email: the MOS orient timer persists
 * a per-day bulletin; this block renders it inside the Morning Brief and
 * offers an on-demand refresh for days the timer hasn't run yet.
 *
 * Anti-hallucination: cards render exactly what the backend composed.
 * An unavailable desk shows "unavailable" — never a filled-in number.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../lib/api';
import { Gauge } from './viz/Gauge';
import { Sparkline } from './viz/Sparkline';

// payload fields are pane-shaped dicts; read defensively without inventing values
function g(obj: unknown, ...path: (string | number)[]): unknown {
  let cur: unknown = obj;
  for (const key of path) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string | number, unknown>)[key];
  }
  return cur;
}

function num(v: unknown, digits = 2): string {
  return typeof v === 'number' ? v.toFixed(digits) : '—';
}

function str(v: unknown): string {
  return typeof v === 'string' && v ? v : '—';
}

export function BulletinCards() {
  const [status, setStatus] = useState<api.BulletinStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.bulletin
      .today()
      .then((s) => { if (alive) setStatus(s); })
      .catch((e) => { if (alive) setErr(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, []);

  async function generate() {
    setBusy(true);
    setErr(null);
    try {
      const b = await api.bulletin.generate();
      setStatus({ exists: true, day: b.day, bulletin: b });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const b = status?.bulletin ?? null;

  return (
    <div data-testid="bulletin">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold">Agent bulletin</h2>
        <div className="flex items-center gap-2">
          {b && (
            <span className="text-[10px] text-ink-mute num" title={b.generated_at}>
              {b.source === 'orient' ? '6am run' : 'on demand'} · {b.day}
            </span>
          )}
          <button onClick={generate} disabled={busy} className="btn text-xs" data-testid="bulletin-generate">
            {busy ? 'Composing…' : b ? 'Refresh' : 'Generate now'}
          </button>
        </div>
      </div>

      {err && <div className="card p-3 text-xs text-warn border-warn mb-3">{err}</div>}

      {!b && !err && (
        <div className="card p-4 text-sm text-ink-mute mb-3" data-testid="bulletin-empty">
          {status === null
            ? 'Loading bulletin…'
            : "No bulletin yet today — the 6am orient run writes one, or generate it now."}
        </div>
      )}

      {b && b.action_items.length > 0 && (
        <div className="card p-3 mb-3 border-edge" data-testid="bulletin-actions">
          <div className="text-xs text-ink-mute mb-1">Action items</div>
          <ul className="space-y-1">
            {b.action_items.map((a, i) => <ActionItem key={i} item={a} />)}
          </ul>
        </div>
      )}

      {b && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 mb-1">
          {b.cards.map((c) => <AgentCard key={c.id} card={c} />)}
        </div>
      )}
    </div>
  );
}

/** v0.5.7 AE-097: an action item is a decision, not an announcement.
 * When the backend supplies a deeplink the row gains a one-tap button
 * that lands the user on the surface where the action is taken. */
const ACTION_LABELS: Record<string, string> = {
  pending_orders: 'Review orders',
  thesis_at_risk: 'Open thesis',
  filing: 'Research',
  deposit: 'Review',
  tlh: 'Open Tax tab',
  recommendation: 'Decide',
};

function ActionItem({ item }: { item: api.BulletinActionItem }) {
  const navigate = useNavigate();
  return (
    <li
      className={`flex items-center justify-between gap-2 text-xs ${
        item.severity === 'high' ? 'text-warn font-medium' : 'text-ink'
      }`}
    >
      <span>
        {item.severity === 'high' ? '⚠ ' : '· '}
        {item.text}
      </span>
      {item.deeplink && (
        <button
          onClick={() => navigate(item.deeplink!)}
          className="btn text-[10px] shrink-0"
          data-testid={`bulletin-action-${item.kind}`}
        >
          {ACTION_LABELS[item.kind] ?? 'Act'} →
        </button>
      )}
    </li>
  );
}

function AgentCard({ card }: { card: api.BulletinCard }) {
  return (
    <div className="card p-3" data-testid={`bulletin-card-${card.id}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold">{card.title}</span>
        {!card.available && (
          <span className="pill text-[10px] text-ink-mute" title={card.error ?? undefined}>unavailable</span>
        )}
      </div>
      {card.available ? <CardBody card={card} /> : (
        <div className="text-[11px] text-ink-mute">This desk couldn't answer{card.error ? `: ${card.error}` : '.'}</div>
      )}
    </div>
  );
}

function Row({ label, value, accent }: { label: string; value: string; accent?: 'ok' | 'warn' | 'bad' }) {
  return (
    <div className="flex items-baseline justify-between gap-2 text-[11px]">
      <span className="text-ink-mute">{label}</span>
      <span className={`num ${accent === 'ok' ? 'text-ok' : accent === 'warn' ? 'text-warn' : accent === 'bad' ? 'text-bad' : 'text-ink'}`}>
        {value}
      </span>
    </div>
  );
}

function CardBody({ card }: { card: api.BulletinCard }) {
  const d = card.data;
  switch (card.id) {
    case 'bond_desk':
      return <BondDeskBody data={d} />;
    case 'fx_metals':
      return <FxMetalsBody data={d} />;
    case 'fear_greed':
      return <FearGreedBody data={d} />;
    case 'tax_counsel': {
      const rp = g(d, 'tax_code');
      const tlh = g(d, 'tlh');
      const amended = g(rp, 'amended_within_window') === true;
      return (
        <div className="space-y-0.5">
          <Row label="Title 26" value={amended ? 'recently amended' : 'stable'} accent={amended ? 'warn' : 'ok'} />
          <Row label="last amended" value={str(g(rp, 'latest_amended_on'))} />
          {tlh != null && (
            <>
              <Row label="TLH candidates" value={`${g(tlh, 'n_candidates') ?? '—'}`} />
              <Row label="est. savings" value={`$${num(g(tlh, 'est_tax_savings'))}`} />
            </>
          )}
        </div>
      );
    }
    case 'filings_watch': {
      const nNew = g(d, 'n_new');
      const newMap = (g(d, 'new') ?? {}) as Record<string, unknown[]>;
      const entries = Object.entries(newMap).slice(0, 4);
      return (
        <div className="space-y-0.5">
          <Row label="new filings" value={`${typeof nNew === 'number' ? nNew : '—'}`} accent={typeof nNew === 'number' && nNew > 0 ? 'warn' : undefined} />
          {entries.map(([sym, filings]) => (
            <div key={sym} className="text-[11px] text-ink-mute truncate">
              {sym}: {(filings as unknown[]).map((f) => str(g(f, 'form'))).join(', ')}
            </div>
          ))}
          {typeof nNew === 'number' && nNew === 0 && (
            <div className="text-[11px] text-ink-mute">Nothing new since last look.</div>
          )}
        </div>
      );
    }
    case 'deposits': {
      const found = g(d, 'deposits_found');
      const next = g(d, 'next_predicted');
      const plan = g(d, 'plan');
      const slices = (g(plan, 'slices') ?? []) as unknown[];
      return (
        <div className="space-y-0.5">
          <Row label="deposits (45d)" value={`${typeof found === 'number' ? found : '—'}`} />
          {next != null && (
            <Row
              label="next expected"
              value={`${str(g(next, 'date'))} · $${num(g(next, 'amount_usd'), 0)}`}
            />
          )}
          {plan != null && (
            <>
              <Row
                label="latest allocation"
                value={`$${num(g(plan, 'total_amount_usd'), 0)}`}
                accent={g(plan, 'requires_manual_approval') === true ? 'warn' : undefined}
              />
              {slices.slice(0, 3).map((s, i) => (
                <div key={i} className="text-[11px] text-ink-mute">
                  → {str(g(s, 'bucket'))}: ${num(g(s, 'amount_usd'), 0)} ({str(g(s, 'reason'))})
                </div>
              ))}
            </>
          )}
          {typeof found === 'number' && found === 0 && (
            <div className="text-[11px] text-ink-mute">No cash-ins in the window.</div>
          )}
        </div>
      );
    }
    case 'strategist': {
      const recs = (g(d, 'recommendations') ?? []) as unknown[];
      const strategies = (g(d, 'strategies') ?? []) as unknown[];
      const undecided = recs.filter((r) => g(r, 'order_status') === 'pending_approval');
      return (
        <div className="space-y-0.5">
          <Row label="strategies live" value={`${strategies.length}`} />
          <Row
            label="proposals today"
            value={`${recs.length}`}
            accent={undecided.length > 0 ? 'warn' : undefined}
          />
          {recs.slice(0, 3).map((r, i) => (
            <div key={i} className="text-[11px] text-ink-mute truncate" title={str(g(r, 'rationale'))}>
              {str(g(r, 'side')).toUpperCase()} {str(g(r, 'symbol'))} · {str(g(r, 'strategy_id')).replace(/_/g, ' ')}
              {typeof g(r, 'confidence') === 'number' ? ` · ${Math.round((g(r, 'confidence') as number) * 100)}%` : ''}
            </div>
          ))}
          {recs.length === 0 && (
            <div className="text-[11px] text-ink-mute">No strategy found a trade worth proposing today.</div>
          )}
        </div>
      );
    }
    default:
      return <pre className="text-[10px] text-ink-mute overflow-x-auto">{JSON.stringify(d, null, 1)}</pre>;
  }
}

/** v0.5.8 AE-105: the actual yield curve, drawn from the tenor values the
 * card already carries — not a stylized icon. Tenors missing a value are
 * skipped; fewer than 3 points renders nothing. */
const TENOR_ORDER = ['1m', '3m', '6m', '1y', '2y', '5y', '10y', '30y'] as const;

function YieldCurve({ tenors }: { tenors: unknown }) {
  const pts = TENOR_ORDER.map((t) => ({ t, v: g(tenors, t, 'latest') }))
    .filter((p): p is { t: (typeof TENOR_ORDER)[number]; v: number } => typeof p.v === 'number');
  if (pts.length < 3) return null;

  const w = 150;
  const h = 44;
  const pad = 6;
  const min = Math.min(...pts.map((p) => p.v));
  const max = Math.max(...pts.map((p) => p.v));
  const range = Math.max(1e-9, max - min);
  const xy = pts.map((p, i) => ({
    x: pad + (i / (pts.length - 1)) * (w - pad * 2),
    y: pad + (h - pad * 2) - ((p.v - min) / range) * (h - pad * 2),
  }));
  const path = xy.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const inverted = pts[pts.length - 1].v < pts[0].v;

  return (
    <div data-testid="yield-curve">
      <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} role="img"
        aria-label={`yield curve, ${pts[0].t} ${pts[0].v}% to ${pts[pts.length - 1].t} ${pts[pts.length - 1].v}%`}
        className={inverted ? 'text-warn' : 'text-accent'}>
        <path d={path} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinejoin="round" />
        {xy.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={1.5} fill="currentColor" />)}
      </svg>
      <div className="flex justify-between text-[9px] text-ink-mute num" style={{ width: w }}>
        <span>{pts[0].t}</span>
        <span>{pts[pts.length - 1].t}</span>
      </div>
    </div>
  );
}

/** v0.5.8 AE-105: bond desk drawn, not listed — yield curve from the card
 * payload + 90-day 10y/2s10s sparklines from GET /research/macro/series. */
function BondDeskBody({ data }: { data: Record<string, unknown> | null }) {
  const curve = g(data, 'curve');
  const credit = g(data, 'credit');
  const [series, setSeries] = useState<Record<string, number[]>>({});

  useEffect(() => {
    let alive = true;
    api.research
      .macroSeries(['DGS10', 'T10Y2Y'], 90)
      .then((r) => {
        if (!alive) return;
        const out: Record<string, number[]> = {};
        for (const [sid, s] of Object.entries(r.series)) out[sid] = s.points.map((p) => p.value);
        setSeries(out);
      })
      .catch(() => { /* rows below still stand; charts simply don't render */ });
    return () => { alive = false; };
  }, []);

  return (
    <div className="space-y-1">
      <YieldCurve tenors={g(curve, 'tenors')} />
      <div className="space-y-0.5">
        <Row label="2y" value={`${num(g(curve, 'tenors', '2y', 'latest'))}%`} />
        <Row label="10y" value={`${num(g(curve, 'tenors', '10y', 'latest'))}%`} />
        <Row label="curve" value={str(g(curve, 'shape'))} />
        <Row label="recession signal" value={str(g(curve, 'recession_signal'))} />
        <Row label="HY spread" value={`${num(g(credit, 'hy_oas', 'latest'))}%`} />
        <Row label="credit" value={str(g(credit, 'stress'))} />
      </div>
      {((series.DGS10?.length ?? 0) >= 2 || (series.T10Y2Y?.length ?? 0) >= 2) && (
        <div className="flex gap-3 pt-0.5" data-testid="bond-sparklines">
          {(series.DGS10?.length ?? 0) >= 2 && (
            <div>
              <Sparkline values={series.DGS10} width={64} height={20} tone="accent" aria-label="10y yield 90-day trend" />
              <div className="text-[9px] text-ink-mute num">10y 90d</div>
            </div>
          )}
          {(series.T10Y2Y?.length ?? 0) >= 2 && (
            <div>
              <Sparkline values={series.T10Y2Y} width={64} height={20} tone="accent" aria-label="2s10s spread 90-day trend" />
              <div className="text-[9px] text-ink-mute num">2s10s 90d</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** v0.5.8 AE-105: FX + metals with trends — broad dollar from FRED,
 * gold from the GLD proxy's real bars. */
function FxMetalsBody({ data }: { data: Record<string, unknown> | null }) {
  const fx = g(data, 'fx');
  const metals = g(data, 'metals');
  const [dollar, setDollar] = useState<number[]>([]);
  const [gold, setGold] = useState<number[]>([]);

  useEffect(() => {
    let alive = true;
    api.research
      .macroSeries(['DTWEXBGS'], 90)
      .then((r) => {
        if (alive) setDollar((r.series.DTWEXBGS?.points ?? []).map((p) => p.value));
      })
      .catch(() => { /* rows still stand */ });
    api.market
      .history('GLD', '3m', '1d')
      .then((r) => { if (alive) setGold((r.bars ?? []).map((b) => b.close)); })
      .catch(() => { /* rows still stand */ });
    return () => { alive = false; };
  }, []);

  return (
    <div className="space-y-1">
      <div className="space-y-0.5">
        <Row label="dollar (broad)" value={num(g(fx, 'dollar_broad', 'latest'), 1)} />
        <Row label="regime" value={str(g(fx, 'dollar_regime'))} />
        <Row label="gold (GLD proxy)" value={`$${num(g(metals, 'gold', 'last'))}`} />
        <Row label="silver (SLV proxy)" value={`$${num(g(metals, 'silver', 'last'))}`} />
      </div>
      {(dollar.length >= 2 || gold.length >= 2) && (
        <div className="flex gap-3 pt-0.5" data-testid="fx-sparklines">
          {dollar.length >= 2 && (
            <div>
              <Sparkline values={dollar} width={64} height={20} tone="accent" aria-label="broad dollar 90-day trend" />
              <div className="text-[9px] text-ink-mute num">dollar 90d</div>
            </div>
          )}
          {gold.length >= 2 && (
            <div>
              <Sparkline values={gold} width={64} height={20} tone="accent" aria-label="gold 3-month trend" />
              <div className="text-[9px] text-ink-mute num">gold 3m</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** v0.5.8 AE-103: Fear & Greed as a gauge with memory. Today's reading
 * comes from the card payload (already composed server-side); the 30-day
 * history sparkline comes from GET /sentiment/fear-greed, falling back to
 * any history embedded in the payload. No data → no chart, never a fake. */
function FearGreedBody({ data }: { data: Record<string, unknown> | null }) {
  const embedded = (g(data, 'history') ?? []) as { day?: string; score?: number }[];
  const [history, setHistory] = useState<{ day: string; score: number }[]>(
    () => embedded.filter((h): h is { day: string; score: number } => typeof h.score === 'number' && typeof h.day === 'string'),
  );

  useEffect(() => {
    let alive = true;
    api.sentiment
      .fearGreed(30)
      .then((r) => { if (alive && r.history.length > 0) setHistory(r.history); })
      .catch(() => { /* embedded payload history (if any) still stands */ });
    return () => { alive = false; };
  }, []);

  const score = g(data, 'score');
  const rating = typeof g(data, 'rating') === 'string' ? (g(data, 'rating') as string) : null;
  const approx = g(data, 'approximate') === true;

  return (
    <div className="flex flex-col items-center" data-testid="fear-greed-body">
      {typeof score === 'number' ? (
        <Gauge value={score} caption={rating} width={132} />
      ) : (
        <Row label="index" value="—" />
      )}
      {history.length >= 2 && (
        <div className="mt-1.5 text-center">
          <Sparkline
            values={history.map((h) => h.score)}
            width={120}
            height={24}
            tone="accent"
            area
            aria-label={`fear & greed ${history.length}-day trend`}
          />
          <div className="text-[9px] text-ink-mute num">{history.length}d trend</div>
        </div>
      )}
      {approx && <div className="text-[10px] text-warn mt-1">VIX-derived proxy (CNN unreachable)</div>}
    </div>
  );
}
